/*
 * assets/js/src/index.js — vstupní bod JS vrstvy redesignu z-agency-2026.
 *
 * Jediný globál, který tahle vrstva zakládá, je `window.ZA2026` (kontrakt
 * A7). Legacy `main.js` sype do `window.zagency` a `window.pux`; oddělený
 * namespace je to, podle čeho se pozná, co je čí.
 *
 * Moduly se registrují přes `register()` a spouští jedním `init()` po
 * `DOMContentLoaded`. Důvod je past z TerraHuntu: legacy `zagency.plugins.init()`
 * volá jedenáct inicializátorů bezpodmínečně a každý si sám hlídá selektor.
 * Nová vrstva to dělá opačně — modul se spustí jen tehdy, když jeho kořen
 * v DOM je, takže „běží nad tím dva ovladače" se dá spočítat, ne odhadovat.
 *
 * Odpojení legacy protějšku se NEDĚLÁ tady. Dělá se tím, že šablona
 * nevypíše aktivační značku (`data-landing-slider-init`, `data-fancybox`,
 * `.landing-image-slider`, `.location-item`) — viz DIVERGENCE.md.
 */

import * as blogLoadmore from './blog-loadmore.js';
import * as carousel from './carousel.js';
import * as dock from './dock.js';
import * as formChips from './form-chips.js';
import * as formPreselect from './form-preselect.js';
import * as galerie from './galerie.js';
import * as hero from './hero.js';
import * as hubIlustrace from './hub-ilustrace.js';
import * as kontakt from './kontakt.js';
import * as langDd from './lang-dd.js';
import * as lightbox from './lightbox.js';
import * as lokality from './lokality.js';
import * as menu from './menu.js';
import * as nahoru from './nahoru.js';
import * as pasy from './pasy.js';
import * as paticka from './paticka.js';
import * as polaroidy from './polaroidy.js';
import * as recenze from './recenze.js';
import * as referenceBublina from './reference-bublina.js';
import * as recenzeDavky from './recenze-davky.js';
import * as reveal from './reveal.js';
import * as sneh from './sneh.js';
import * as snehulak from './snehulak.js';
import * as stats from './stats.js';
import * as sticky from './sticky.js';
import * as zdrojNavstevy from './zdroj-navstevy.js';

const modules = [];

/**
 * Zaregistruje modul. `name` je jen pro počítadlo a hlášení, `setup` dostane
 * kořenový prvek. Registrace nic nespouští.
 */
function register(name, selector, setup) {
	modules.push({ name, selector, setup });
}

/**
 * Spustí registrované moduly nad prvky, které v dokumentu skutečně jsou.
 * Vrací počet inicializací per modul — to je to, co se v důkazu integrace
 * počítá proti legacy `.slick(`.
 */
function init(root) {
	const scope = root || document;
	const counts = {};
	for (const m of modules) {
		const nodes = scope.querySelectorAll(m.selector);
		counts[m.name] = 0;
		for (const node of nodes) {
			if (node.dataset.za2026Init === '1') continue;
			node.dataset.za2026Init = '1';
			m.setup(node);
			counts[m.name] += 1;
		}
	}
	return counts;
}

/*
 * SKLIZENÉ MODULY. Každý má vlastní `init()`, které si samo najde své
 * kořeny a VRACÍ počet inicializací. Návratová hodnota není kosmetika:
 * důkaz jedné integrace na ní stojí — porovnává se proti počtu volání
 * legacy `.slick(` nad týmž prvkem.
 */
function initModules() {
	const counts = {
		blogLoadmore: blogLoadmore.init(),
		carousel: carousel.init(),
		dock: dock.init(),
		formChips: formChips.init(),
		formPreselect: formPreselect.init(),
		galerie: galerie.init(),
		hero: hero.init(),
		hubIlustrace: hubIlustrace.init(),
		kontakt: kontakt.init(),
		langDd: langDd.init(),
		lightbox: lightbox.init(),
		lokality: lokality.init(),
		lokalitaDetail: lokality.initDetail(),
		hledani: lokality.initHledani(),
		menu: menu.init(),
		nahoru: nahoru.init(),
		pasy: pasy.init(),
		paticka: paticka.init(),
		polaroidy: polaroidy.init(),
		recenze: recenze.init(),
		referenceBublina: referenceBublina.init(),
		recenzeDavky: recenzeDavky.init(),
		reveal: reveal.init(),
		sneh: sneh.init(),
		snehulak: snehulak.init(),
		stats: stats.init(),
		sticky: sticky.init(),
		zdrojNavstevy: zdrojNavstevy.init(),
	};
	const own = init();
	for (const key of Object.keys(own)) {
		counts[key] = own[key];
	}
	return counts;
}

const ZA2026 = { register, init, initModules, modules, stats: null };

/*
 * JEDINÝ GLOBÁL VRSTVY. Přiřazuje se TADY, ne přes esbuild
 * `--global-name` — a je to opravená chyba, ne preference.
 *
 * S `--global-name=ZA2026` esbuild na `window.ZA2026` posadí JMENNÝ
 * PROSTOR MODULU, takže tenhle objekt skončí pod `window.ZA2026.default`
 * a `window.ZA2026.stats` je `undefined`. Změřeno 16. 9. při důkazu
 * integrace: počítadlo inicializací se tvářilo jako nenaplněné, přestože
 * modul proběhl správně. Selhání měřidla, ne modulu — a přesně ta třída
 * chyby, kterou má důkaz integrace odhalit („doručení skriptů
 * WordPressem" z kontraktu).
 */
window.ZA2026 = ZA2026;

function boot() {
	/*
	 * `stats` se vyplní JEDNOU. Kdyby modul někdo spustil znovu (pozdní
	 * init po AJAXu, druhé zavolání `boot`), přičte se to sem — takže
	 * „žádná pozdní reinicializace" se dá doložit číslem, ne tvrzením.
	 */
	const counts = initModules();

	if (ZA2026.stats === null) {
		ZA2026.stats = { runs: 1, counts };
	} else {
		ZA2026.stats.runs += 1;
		for (const key of Object.keys(counts)) {
			ZA2026.stats.counts[key] = (ZA2026.stats.counts[key] || 0) + counts[key];
		}
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}

/*
 * ŽÁDNÝ `export default`. Vstupní bod bundlu nic neexportuje: kdyby
 * exportoval, vrácené `--global-name` by chybu z 16. 9. zopakovalo
 * potichu. Jediná veřejná cesta k modulu je `window.ZA2026` výš.
 */
